import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CHUNK_UPDATE_EVENT, isChunkLoadError, notifyChunkUpdate } from '../router/lazyWithRetry';

const VERSION_POLL_INTERVAL_MS = 60 * 1000;
const VERSION_INITIAL_DELAY_MS = 15 * 1000;
const IDLE_THRESHOLD_MS = 30 * 1000;
const IDLE_CHECK_INTERVAL_MS = 5 * 1000;
const ENABLE_CHUNK_UPDATE_PROMPT = process.env.NODE_ENV === 'production';

const buttonBaseStyle: React.CSSProperties = {
  height: 36,
  borderRadius: 8,
  padding: '0 14px',
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#0f172a',
  cursor: 'pointer',
};

const isChunkScriptTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLScriptElement)) {
    return false;
  }

  return /\/static\/js\/.+\.chunk\.js($|\?)/.test(target.src);
};

const getVersionSignature = async () => {
  const response = await fetch(`/asset-manifest.json?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      pragma: 'no-cache',
      'cache-control': 'no-cache',
    },
  });

  if (!response.ok) {
    throw new Error(`获取版本清单失败: ${response.status}`);
  }

  const manifest = (await response.json()) as {
    files?: Record<string, string>;
    entrypoints?: string[];
  };

  const files = manifest.files || {};
  const entrypoints = Array.isArray(manifest.entrypoints) ? manifest.entrypoints : [];

  return JSON.stringify({
    mainJs: files['main.js'] || '',
    mainCss: files['main.css'] || '',
    runtime: files['runtime-main.js'] || '',
    entrypoints,
  });
};

const isInteractiveElementFocused = () => {
  const activeElement = document.activeElement as HTMLElement | null;
  if (!activeElement) {
    return false;
  }

  const tagName = activeElement.tagName.toLowerCase();
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    activeElement.isContentEditable
  );
};

const ChunkUpdatePrompt: React.FC = () => {
  const [pendingUpdate, setPendingUpdate] = useState<{ key: string; message?: string } | null>(null);
  const [bannerVisible, setBannerVisible] = useState(false);
  const baselineVersionRef = useRef<string | null>(null);
  const lastActivityAtRef = useRef(Date.now());
  const dismissedUpdateKeyRef = useRef<string | null>(null);

  const markActive = useCallback(() => {
    lastActivityAtRef.current = Date.now();
  }, []);

  const shouldShowBanner = useCallback((updateKey: string) => {
    if (dismissedUpdateKeyRef.current === updateKey) {
      return false;
    }

    if (isInteractiveElementFocused()) {
      return false;
    }

    return Date.now() - lastActivityAtRef.current >= IDLE_THRESHOLD_MS;
  }, []);

  const registerUpdate = useCallback((payload: { key: string; message?: string }) => {
    setPendingUpdate((current) => {
      if (current?.key === payload.key) {
        return current;
      }
      return payload;
    });
  }, []);

  useEffect(() => {
    if (!ENABLE_CHUNK_UPDATE_PROMPT) {
      return;
    }

    const handleChunkEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ key: string; message?: string }>;
      if (customEvent.detail?.key) {
        registerUpdate(customEvent.detail);
      }
    };

    const handleWindowError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error || event.message) || isChunkScriptTarget(event.target)) {
        notifyChunkUpdate({
          key: 'window-chunk-error',
          message: typeof event.message === 'string' ? event.message : '',
        });
      }
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        notifyChunkUpdate({
          key: 'unhandled-chunk-rejection',
          message: event.reason instanceof Error ? event.reason.message : '',
        });
      }
    };

    window.addEventListener(CHUNK_UPDATE_EVENT, handleChunkEvent as EventListener);
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener(CHUNK_UPDATE_EVENT, handleChunkEvent as EventListener);
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [registerUpdate]);

  useEffect(() => {
    if (!ENABLE_CHUNK_UPDATE_PROMPT) {
      return;
    }

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'focus',
    ];

    activityEvents.forEach((eventName) => window.addEventListener(eventName, markActive, { passive: true }));

    return () => {
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, markActive));
    };
  }, [markActive]);

  useEffect(() => {
    if (!ENABLE_CHUNK_UPDATE_PROMPT) {
      return;
    }

    let cancelled = false;
    let intervalId: number | undefined;

    const initializeVersion = async () => {
      try {
        const signature = await getVersionSignature();
        if (!cancelled) {
          baselineVersionRef.current = signature;
        }
      } catch (_error) {
        // 忽略初始化失败，避免影响首屏
      }
    };

    const pollVersion = async () => {
      try {
        const nextSignature = await getVersionSignature();
        const previousSignature = baselineVersionRef.current;
        if (!previousSignature) {
          baselineVersionRef.current = nextSignature;
          return;
        }

        if (previousSignature !== nextSignature) {
          notifyChunkUpdate({
            key: `manifest:${nextSignature}`,
            message: '检测到新版本前端资源，建议刷新后继续使用。',
          });
          baselineVersionRef.current = nextSignature;
        }
      } catch (_error) {
        // 忽略轮询失败，避免弱网时反复打扰
      }
    };

    const timeoutId = window.setTimeout(() => {
      void initializeVersion();
      intervalId = window.setInterval(() => {
        void pollVersion();
      }, VERSION_POLL_INTERVAL_MS);
    }, VERSION_INITIAL_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, []);

  useEffect(() => {
    if (!ENABLE_CHUNK_UPDATE_PROMPT) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!pendingUpdate) {
        return;
      }

      if (shouldShowBanner(pendingUpdate.key)) {
        setBannerVisible(true);
      }
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [pendingUpdate, shouldShowBanner]);

  useEffect(() => {
    if (!ENABLE_CHUNK_UPDATE_PROMPT) {
      return;
    }

    if (!pendingUpdate) {
      setBannerVisible(false);
      return;
    }

    if (shouldShowBanner(pendingUpdate.key)) {
      setBannerVisible(true);
    }
  }, [pendingUpdate, shouldShowBanner]);

  if (!ENABLE_CHUNK_UPDATE_PROMPT || !pendingUpdate || !bannerVisible) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        left: 16,
        right: 16,
        zIndex: 3000,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div style={{ width: '100%', maxWidth: 880, pointerEvents: 'auto' }}>
        <div
          style={{
            borderRadius: 16,
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.16)',
            border: '1px solid #bfdbfe',
            background: '#eff6ff',
            padding: 16,
            color: '#1e3a8a',
          }}
          role="status"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <strong>版本已更新</strong>
              <span style={{ color: '#475569' }}>
                {pendingUpdate.message || '检测到新版本前端资源，点击立即刷新后即可使用最新版本。'}
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{
                    ...buttonBaseStyle,
                    borderColor: '#2563eb',
                    background: '#2563eb',
                    color: '#fff',
                  }}
                >
                  立即刷新
                </button>
                <button
                  type="button"
                  onClick={() => {
                    dismissedUpdateKeyRef.current = pendingUpdate.key;
                    setBannerVisible(false);
                  }}
                  style={buttonBaseStyle}
                >
                  稍后提醒
                </button>
              </div>
            </div>
            <button
              type="button"
              aria-label="关闭版本更新提示"
              onClick={() => {
                dismissedUpdateKeyRef.current = pendingUpdate.key;
                setBannerVisible(false);
              }}
              style={{
                width: 32,
                height: 32,
                border: 0,
                background: 'transparent',
                color: '#64748b',
                fontSize: 20,
                lineHeight: '32px',
                cursor: 'pointer',
              }}
            >
              x
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChunkUpdatePrompt;
